import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Image from 'next/image';
import Conditional from '@next-app/components/Conditional';
import LLCheckbox from '@next-app/components/elements/LLCheckbox/LLCheckbox';
import Link from '@next-app/components/elements/Link/Link';
import {
  getFormattedPrice,
  isNotNullOrUndefined,
} from '@next-app/utils/common-utils';
import {
  selectDisablePaymentList,
  selectPaymentDetailInfo,
  selectPaymentsInfo,
} from '@next-app/lib/features/checkout/CheckoutSelector';
import {
  applyGiftCardReq,
  handleCheckoutServerErrors,
  maskGiftCardNumber,
  removeGiftCardReq,
  triggerCheckoutFormErrorEvent,
} from '@next-app/utils/CheckoutHelper';
import {
  GiftCertificate,
  ProfileGiftCard,
} from '@next-app/lib/features/checkout/Checkout.interface';
import GiftCardForm from '../GiftCardForm/GiftCardForm';
import { setLoadCheckoutDetails } from '@next-app/lib/features/checkout/CheckoutSlice';
import {
  selectDynSess,
  selectIsFurniture,
  selectIsGuestUser,
  selectLlmAPIVersion,
} from '@next-app/lib/features/InitSelectors';
import BopisStorePickupInfo from '../BopisStorePickupInfo/BopisStorePickupInfo';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import Loader from '@next-app/components/shared/Loader/Loader';
import { GiftCardFormFields } from '../GiftCardForm/GiftCardFormFields';

const GiftCardsList = () => {
  const dispatch = useDispatch();
  const dynSessConf = useSelector(selectDynSess);
  // selectors
  const { giftCertificates, profileGiftcards, giftCardsTotalAmount } =
    useSelector(selectPaymentsInfo) || {};
  const [loading, setLoading] = useState<boolean>(false);
  const { additionalPaymentRequired } =
    useSelector(selectPaymentDetailInfo) || {};
  const disablePaymentList = useSelector(selectDisablePaymentList);
  const isGuestUser = useSelector(selectIsGuestUser);
  const isFurniture = useSelector(selectIsFurniture);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);

  const gcRemainingBalance = parseFloat(
    (
      giftCertificates?.reduce(
        (totalBalanceLeft, gcItem) =>
          totalBalanceLeft + (gcItem?.balance - gcItem.amount),
        0,
      ) + giftCardsTotalAmount
    ).toFixed(2),
  );

  // local states
  const [hideAddGCForm, setHideAddGCForm] = useState(true);
  const [giftCardErrors, setGiftCardErrors] =
    useState<Record<string, string>>();

  // methods
  // handleAddGiftCard
  const handleAddGiftCard = () => {
    setHideAddGCForm((prev) => !prev);
  };

  const removeGiftCard = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const gcNumber = (e.target as HTMLInputElement)?.id;
      if (gcNumber) {
        setLoading(true);
        const removeGiftCardBody = {
          gcNumber: [gcNumber],
          _dynSessConf: dynSessConf,
          howMany: -1,
          start: 0,
        };
        const removeGiftCardResponse = await removeGiftCardReq(
          removeGiftCardBody,
          isFurniture,
        );
        if (removeGiftCardResponse?.checkoutResponse) {
          dispatch(
            setLoadCheckoutDetails(removeGiftCardResponse.checkoutResponse),
          );
        }
        if (removeGiftCardResponse?.formError) {
          handleCheckoutServerErrors(
            removeGiftCardResponse.formExceptions,
            setGiftCardErrors,
          );
          triggerCheckoutFormErrorEvent({
            step: 'payment',
            llmAPIVersion,
            isFurniture,
            errors: removeGiftCardResponse?.formExceptions,
          });
        }
      }
    } catch (error) {
      console.error('Failed to remove gift card', error);
    } finally {
      setLoading(false);
    }
  };

  const selectGiftCard = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // savedGiftcardId
    setLoading(true); // Start loader
    try {
      const repoId = (e.target as HTMLInputElement)?.id;
      if (repoId) {
        const selectGiftCardRequest = {
          savedGiftcardId: repoId,
          _dynSessConf: dynSessConf,
          howMany: -1,
          start: 0,
        };
        const applyGiftCardResponse = await applyGiftCardReq(
          selectGiftCardRequest,
          isFurniture,
        );

        if (applyGiftCardResponse?.checkoutResponse) {
          const eventData = {
            event: 'paymentWalletGC',
          };
          DynamicDTMEvent(eventData);
          dispatch(
            setLoadCheckoutDetails(applyGiftCardResponse.checkoutResponse),
          );
        }
        if (applyGiftCardResponse?.formError) {
          handleCheckoutServerErrors(
            applyGiftCardResponse.formExceptions,
            setGiftCardErrors,
          );
        }
      }
    } catch (error) {
      console.error('Failed to apply gift card', error);
    } finally {
      setLoading(false);
    }
  };

  const giftCardItem = (
    isSelected: boolean,
    repositoryId: string,
    gcNumber: string,
    balance: string,
    amount?: number,
  ) => {
    const showGiftCard =
      isNotNullOrUndefined(gcNumber) &&
      isNotNullOrUndefined(balance) &&
      isSelected
        ? isNotNullOrUndefined(amount)
        : isNotNullOrUndefined(repositoryId);

    return (
      <>
        <Conditional key={repositoryId} if={showGiftCard}>
          <div className="mb-ll-5">
            <LLCheckbox
              id={isSelected ? gcNumber : repositoryId}
              checked={isSelected}
              onChange={isSelected ? removeGiftCard : selectGiftCard}
              disabled={!isSelected && disablePaymentList ? true : false}
              customClass="d-flex align-items-center custom-disabled"
              errorMsg={giftCardErrors?.[GiftCardFormFields.GENERIC_ERROR]}
            >
              <div className="d-flex align-items-center ms-1">
                <Image
                  src="/react/assets/icons/gift-card.svg"
                  alt="Gift Card icon"
                  unoptimized={true}
                  className="ms-ll-10"
                  width={48}
                  height={25}
                />

                <div className="d-flex flex-wrap ms-1">
                  <strong className="fs-base lh-base me-1">{`${balance}`}</strong>
                  <Conditional
                    if={
                      isSelected &&
                      parseFloat(balance.replace('$', '')) !== amount
                    }
                  >
                    <span className="me-1">{`(${getFormattedPrice(amount)} Applied)`}</span>
                  </Conditional>
                  <span
                    style={{ color: '#717171' }}
                    className="ms-ll-10"
                  >{`(${maskGiftCardNumber(gcNumber)})`}</span>
                </div>
              </div>
            </LLCheckbox>
          </div>
        </Conditional>
        {/* Loading Indicator */}
        <Conditional if={loading}>
          <Loader aria-label="Remove gift card request" />
        </Conditional>
      </>
    );
  };

  return (
    <>
      <Conditional if={giftCertificates?.length > 0 && gcRemainingBalance > 0}>
        <div className="mb-ll-3">
          <span>
            <strong>{getFormattedPrice(gcRemainingBalance)}</strong>
            {` in available gift cards`}
          </span>
        </div>
      </Conditional>
      {giftCertificates?.map((giftCard: GiftCertificate) => {
        return giftCardItem(
          true,
          giftCard.id,
          giftCard.giftCardNumber,
          getFormattedPrice(giftCard.balance),
          giftCard.amount,
        );
      })}
      {profileGiftcards?.map((giftCard: ProfileGiftCard) => {
        return giftCardItem(
          false,
          giftCard.repositoryId,
          giftCard.giftcardNumber,
          giftCard.amount,
        );
      })}

      <Conditional if={!(isGuestUser && !additionalPaymentRequired)}>
        <>
          <Conditional if={hideAddGCForm}>
            <Link className={`btn btn-link`} onClick={handleAddGiftCard}>
              {'Add Gift Card'}
            </Link>
          </Conditional>
          <Conditional if={!hideAddGCForm}>
            <GiftCardForm setShowCollapsed={setHideAddGCForm} />
          </Conditional>
        </>
      </Conditional>
      <BopisStorePickupInfo />
    </>
  );
};

export default GiftCardsList;
